import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react"
import {useAppKitAccount} from "@reown/appkit/react"
import {useAuthLazyQuery} from "@/hooks/useAuthApi.ts";
import {useLazyBalanceQuery} from "@/api/endpoints/solana.ts";
import {TokenBalance} from "@/common/types/api/solana.types.api.ts";



type SolanaBalanceContextType = {
  balanceSol: { amount: bigint; amountUi: number } | null;
  balanceZnx: { amount: bigint; amountUi: number } | null;
  balanceUsdc: { amount: bigint; amountUi: number } | null;
  balanceAll: TokenBalance[];
  refreshBalance: () => Promise<void>
  catchBalanceChange: () => Promise<void>
};

const SolanaBalanceContext = createContext<SolanaBalanceContextType | undefined>(undefined)


export const SolanaBalanceProvider = ({children}: { children: React.ReactNode }) => {
  const [balanceSol, setBalanceSol] = useState<{amount: bigint, amountUi: number} | null>(null)
  const [balanceZnx, setBalanceZnx] = useState<{amount: bigint, amountUi: number} | null>(null)
  const [balanceUsdc, setBalanceUsdc] = useState<{amount: bigint, amountUi: number} | null>(null)
  const [balanceAll, setBalanceAll] = useState<TokenBalance[]>([])

  const {isConnected, address} = useAppKitAccount({namespace: 'solana'})
  const [triggerBalanceQuery] = useAuthLazyQuery(useLazyBalanceQuery)

  const addressRef = useRef(address);
  const isConnectedRef = useRef(isConnected);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const catchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearAllTimers = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (catchTimerRef.current) clearTimeout(catchTimerRef.current);
    timerRef.current = null;
    catchTimerRef.current = null;
  }, []);

  const refreshBalance = useCallback(async (forcedAddress?: string) => {

    console.log("refreshBalance", forcedAddress);
    const targetAddress = forcedAddress || address;
    if (!isConnected || !targetAddress) return;

    try {
      const {data: balanceData} = await triggerBalanceQuery({address: targetAddress}, true);

      // Проверка через Ref — единственная защита от Race Condition
      if (targetAddress !== addressRef.current) return;

      if (balanceData) {
        setBalanceAll(balanceData.token || [])
        setBalanceSol({amount: balanceData.sol, amountUi: balanceData.sol_ui})
        setBalanceZnx({amount: balanceData.znx, amountUi: balanceData.znx_ui})
        setBalanceUsdc({amount: balanceData.usdc, amountUi: balanceData.usdc_ui})
      }
    } catch (e) {
      console.error("Refresh failed", e);
    }
  }, [isConnected, address, triggerBalanceQuery]);

  const startMainTimer = useCallback((targetAddress: string) => {
    clearAllTimers();
    if (!isConnectedRef.current) return;
    timerRef.current = setInterval(() => refreshBalance(targetAddress), 30000);
  }, [clearAllTimers, refreshBalance]);

  const catchBalanceChange = useCallback(async () => {
    const targetAddress = address;
    if (!targetAddress || !isConnected) return;

    clearAllTimers();
    /** Паузы между четырьмя подряд запросами (после 1–3 шага). */
    const delaysBetweenStepsMs = [2000, 3000, 4000];

    const runStep = async (index: number) => {
      if (targetAddress !== addressRef.current) return;

      await refreshBalance(targetAddress);

      if (index < delaysBetweenStepsMs.length) {
        catchTimerRef.current = setTimeout(() => {
          runStep(index + 1);
        }, delaysBetweenStepsMs[index]);
      } else if (isConnectedRef.current) {
        startMainTimer(targetAddress);
      }
    };

    runStep(0);
  }, [address, isConnected, clearAllTimers, refreshBalance, startMainTimer]);

  // ЕДИНСТВЕННЫЙ эффект для управления жизненным циклом адреса
  useEffect(() => {
    // 1. Синхронизируем Ref и чистим стейт
    addressRef.current = address;
    isConnectedRef.current = isConnected;
    setBalanceAll([]);
    setBalanceSol(null);
    setBalanceZnx(null);
    setBalanceUsdc(null);
    clearAllTimers();

    if (address && isConnected) {
      // 2. Запускаем стандартный цикл
      refreshBalance(address);
      startMainTimer(address);
    }

    return () => clearAllTimers();
  }, [address, isConnected, clearAllTimers, refreshBalance, startMainTimer]);

  const contextValue = useMemo(() => ({
    balanceSol,
    balanceZnx,
    balanceUsdc,
    balanceAll,
    refreshBalance: () => refreshBalance(address),
    catchBalanceChange
  }), [balanceSol, balanceZnx,balanceUsdc, balanceAll, refreshBalance, address, catchBalanceChange]);

  return (
    <SolanaBalanceContext.Provider value={contextValue}>
      {children}
    </SolanaBalanceContext.Provider>
  )
}

export const useSolanaBalance = () => {
  const ctx = useContext(SolanaBalanceContext);
  if (ctx === undefined) {
    throw new Error("useSolanaBalance must be used within SolanaBalanceProvider");
  }
  return ctx;
};

